/**
 * BottomTabBar — barra di navigazione mobile §9.5.
 *
 * 5 voci nell'ordine fisso (Home, Agenda, Pazienti, Fatture, Altro).
 * "Altro" non naviga: apre un BottomSheet con i 4 link secondari
 * (Piani Alimentari, Comunicazioni, Statistiche, Impostazioni).
 *
 * Tab attiva: testo+icona `primary` + linea sopra `bg-primary` 2px.
 * Touch target ≥ 56×44 (più alto della baseline 44 per safe-area).
 *
 * `md:hidden`: scompare ≥768px (la navigazione passa ad AppSidebar).
 */

import { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuth } from '../../hooks/useAuth';
import {
  BOTTOM_TAB_NAV,
  filterNavItemsForRole,
  MORE_TAB_NAV,
  type NavItem,
} from '../../lib/nav-items';
import { Icon } from '@shared/components/ui/Icon';
import { BottomSheet } from '../ui/BottomSheet';

type BottomTabEntry = (typeof BOTTOM_TAB_NAV)[number];

function isMoreAction(
  entry: BottomTabEntry
): entry is { label: string; icon: string; action: 'more' } {
  return 'action' in entry && entry.action === 'more';
}

export function BottomTabBar() {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { role } = useAuth();
  const moreItems = useMemo(
    () => filterNavItemsForRole(MORE_TAB_NAV, role),
    [role]
  );

  // "Altro" è considerata attiva se la pagina corrente è una delle voci
  // secondarie VISIBILI per il ruolo.
  const moreActive = useMemo(
    () => moreItems.some((it) => location.pathname.startsWith(it.to)),
    [location.pathname, moreItems]
  );

  const handleMoreNavigate = (to: string) => {
    setMoreOpen(false);
    navigate(to);
  };

  return (
    <>
      <nav
        aria-label="Navigazione principale mobile"
        className="bottom-tab-bar md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-border-gray pb-[env(safe-area-inset-bottom)]"
      >
        <ul className="grid grid-cols-5">
          {BOTTOM_TAB_NAV.map((item) => {
            if (isMoreAction(item)) {
              return (
                <li key="more">
                  <button
                    type="button"
                    onClick={() => setMoreOpen(true)}
                    aria-label={item.label}
                    aria-current={moreActive ? 'page' : undefined}
                    className={clsx(
                      'relative w-full min-h-[56px] flex flex-col items-center justify-center gap-0.5 px-1 transition-colors',
                      moreActive
                        ? 'text-primary'
                        : 'text-text-secondary hover:text-text-main',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-magenta-soft'
                    )}
                  >
                    {moreActive && (
                      <span
                        aria-hidden
                        className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full"
                      />
                    )}
                    <Icon name={item.icon} size={24} />
                    <span className="text-[11px] font-semibold">
                      {item.label}
                    </span>
                  </button>
                </li>
              );
            }
            const navItem: NavItem = item;
            return (
              <li key={navItem.to}>
                <NavLink
                  to={navItem.to}
                  end={navItem.exact}
                  aria-label={navItem.label}
                  className={({ isActive }) =>
                    clsx(
                      'relative w-full min-h-[56px] flex flex-col items-center justify-center gap-0.5 px-1 transition-colors',
                      isActive
                        ? 'text-primary'
                        : 'text-text-secondary hover:text-text-main',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-magenta-soft'
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span
                          aria-hidden
                          className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full"
                        />
                      )}
                      <Icon name={navItem.icon} size={24} />
                      <span className="text-[11px] font-semibold">
                        {navItem.label}
                      </span>
                    </>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      <BottomSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        title="Altre sezioni"
      >
        <ul className="divide-y divide-border-gray/60">
          {moreItems.map((item) => {
            const active = location.pathname.startsWith(item.to);
            return (
              <li key={item.to}>
                <button
                  type="button"
                  onClick={() => handleMoreNavigate(item.to)}
                  className={clsx(
                    'w-full flex items-center gap-3 min-h-[56px] py-3 text-left transition-colors rounded-lg',
                    active
                      ? 'text-primary'
                      : 'text-text-main hover:bg-bg-app'
                  )}
                >
                  <Icon
                    name={item.icon}
                    size={24}
                    className={active ? 'text-primary' : 'text-text-secondary'}
                  />
                  <span className="font-body-md font-semibold">
                    {item.label}
                  </span>
                  <Icon
                    name="chevron_right"
                    size={20}
                    className="ml-auto text-text-secondary"
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </BottomSheet>
    </>
  );
}
